import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/* global Theme */

const STATUS_CLEAR_DELAY = 4000;
const PICKED_FILTER = '__picked';
const EVENT_SOURCE = 'puf-stand-builder';
const BAR_HEIGHT_PROPERTY = '--puf-stand-bar-height';
const SHOE_PROPERTY = '--puf-shoe';
/** Most charms ever placed on the clog drawings (a pair has 26 holes). */
const PREVIEW_LIMIT = 40;
const SVG_NS = 'http://www.w3.org/2000/svg';
/** Session flag: the "you can drag a charm" demo has played once. */
const DEMO_KEY = 'puf-crocs-drag-demo';
const HINT_DURATION = 7000;

/**
 * Ease-in-out for the demo movement.
 * @param {number} t - 0..1
 * @returns {number}
 */
function ease(t) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

/**
 * @typedef {object} Tier
 * @property {number} min - Total pieces from which the tier applies.
 * @property {number} price - Piece price of the cheapest model, in minor units.
 * @property {number} off - Amount every piece gets off its own price in this tier, in minor units.
 *
 * @typedef {object} Config
 * @property {number} productId
 * @property {number} step - Quantity step per model.
 * @property {number} min - Minimum total pieces for an order.
 * @property {Tier[]} tiers
 * @property {string[]} choices - Values of the option chosen once for the whole order, if any.
 * @property {boolean} varied - Whether models are priced differently from each other.
 * @property {string} moneyFormat
 * @property {string} currency
 * @property {Record<string, string>} strings - Texts with `[name]` style placeholders.
 *
 * @typedef {object} CardVariant
 * @property {string} id
 * @property {number} price - Minor units.
 *
 * @typedef {object} Card
 * @property {HTMLElement} element
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement | null} minus
 * @property {Element | null} priceLabel
 * @property {Record<string, CardVariant>} variants - By order-wide choice; a single '' key without one.
 * @property {string} name
 * @property {string} group
 * @property {string} search - Lower-cased name used for searching.
 * @property {string} face - Small transparent image placed on the clog preview; '' without one.
 */

/**
 * Fills `[name]` placeholders of a translated text.
 * @param {string} template
 * @param {Record<string, string | number>} values
 * @returns {string}
 */
function fill(template = '', values = {}) {
  return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
}

/**
 * Lower-cases text for searching, treating Turkish dotted and dotless i alike.
 * @param {string} text
 * @returns {string}
 */
function searchable(text) {
  return text.toLocaleLowerCase('tr').replaceAll('ı', 'i').trim();
}

/**
 * "Kendi standını oluştur" builder. Every card is a model of one product; the shopper sets
 * a quantity per card and the component keeps the totals, the tiered piece price and the
 * next-tier hint current. Submitting writes all quantities of this product to the cart in a
 * single request and announces it with the standard cart event, so the cart drawer and cart
 * icon refresh as they do for the theme's own product form.
 *
 * A card holds one variant, or one per value of an order-wide choice (for example with or
 * without a lighter). The choice applies to every model, so changing it swaps all of this
 * product's cart lines to the matching variants on the next submit.
 *
 * Quantities already in the cart are loaded on connect, so returning to the page continues
 * the same order instead of adding to it twice.
 */
class PufStandBuilder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ResizeObserver | undefined} */
  #barObserver;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Config | undefined} */
  #config;
  /** @type {Map<string, Card>} */
  #cards = new Map();
  /** Quantities chosen on the page, by card ID. @type {Map<string, number>} */
  #quantities = new Map();
  /** Current order-wide choice; '' when the product has none. */
  #choice = '';
  /** Quantities currently in the cart, by variant ID. @type {Map<string, number>} */
  #cartQuantities = new Map();
  #filter = '';
  #query = '';
  #busy = false;
  /** Card id sitting in each hole of the clog preview, by slot index; null when empty. @type {(string | null)[]} */
  #arrangement = [];
  /** @type {{ index: number, slot: SVGImageElement, ghost: SVGImageElement, svg: SVGSVGElement, preview: Element, pointerId: number, target: number | null } | null} */
  #drag = null;
  /** Whether the drag demo has played (or is queued) on this page. */
  #demoDone = false;
  /** Cancels a running drag demo. @type {(() => void) | null} */
  #demoCancel = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hintTimer;

  connectedCallback() {
    const configText = this.querySelector('script[data-config]')?.textContent;
    if (!configText) return;

    /** @type {Config} */
    const config = JSON.parse(configText);
    config.tiers = config.tiers.filter((tier) => tier.min > 0 && tier.price > 0).sort((a, b) => a.min - b.min);
    for (const tier of config.tiers) tier.off = (config.tiers[0]?.price ?? tier.price) - tier.price;
    this.#config = config;
    this.#choice = config.choices[0] ?? '';

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#cards.clear();
    for (const element of this.querySelectorAll('[data-card]')) {
      const input = element.querySelector('[data-qty]');
      if (!(element instanceof HTMLElement) || !(input instanceof HTMLInputElement)) continue;

      const { id = '', name = '', group = '', variants = '{}', face = '' } = element.dataset;
      const minus = element.querySelector('[data-minus]');
      /** @type {Record<string, CardVariant>} */
      const byChoice = {};
      for (const [choice, variant] of Object.entries(JSON.parse(variants))) {
        byChoice[choice] = { id: String(variant.id), price: Number(variant.price) };
      }

      this.#cards.set(id, {
        element,
        input,
        minus: minus instanceof HTMLButtonElement ? minus : null,
        priceLabel: element.querySelector('[data-card-price]'),
        variants: byChoice,
        name,
        group,
        search: searchable(name),
        face,
      });
    }

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    document.addEventListener(CartLinesUpdateEvent.eventName, this.#onCartUpdate, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    // A long press on a charm (how you start dragging on a phone) must not open the browser's
    // image menu ("Resmi indir…" on Android Chrome).
    this.addEventListener('contextmenu', this.#onContextMenu, { signal });

    let slotCount = 0;
    for (const preview of this.querySelectorAll('[data-preview]')) {
      slotCount = Math.max(slotCount, preview.querySelectorAll('[data-slot]').length);
    }
    this.#arrangement = new Array(slotCount).fill(null);

    this.#watchBar();
    this.#labelTiers();
    this.#labelChoices();
    this.#render();
    this.#loadCart(true);
  }

  disconnectedCallback() {
    if (this.#drag) this.#endDrag(false);
    this.#demoCancel?.();
    clearTimeout(this.#hintTimer);
    this.#controller?.abort();
    this.#barObserver?.disconnect();
    clearTimeout(this.#timer);
    document.documentElement.style.removeProperty(BAR_HEIGHT_PROPERTY);
  }

  /** Publishes the fixed bar's height so the page and the chat button can clear it. */
  #watchBar() {
    const bar = this.querySelector('.puf-stand__bar');
    this.#barObserver?.disconnect();
    if (!(bar instanceof HTMLElement)) return;

    this.#barObserver = new ResizeObserver(() => {
      const height = bar.offsetHeight;
      if (height > 0) {
        document.documentElement.style.setProperty(BAR_HEIGHT_PROPERTY, `${height}px`);
      } else {
        document.documentElement.style.removeProperty(BAR_HEIGHT_PROPERTY);
      }
    });
    this.#barObserver.observe(bar);
  }

  /** Writes the piece range ("100–199 adet") into each tier of the price ladder. */
  #labelTiers() {
    const config = this.#config;
    if (!config) return;

    config.tiers.forEach((tier, index) => {
      const next = config.tiers[index + 1];
      const label = next
        ? fill(config.strings.tierRange, { from: tier.min, to: next.min - 1 })
        : fill(config.strings.tierOpen, { from: tier.min });

      for (const target of this.querySelectorAll(`[data-tier="${tier.min}"] [data-tier-range]`)) {
        target.textContent = label;
      }
    });
  }

  /** Writes what each order-wide choice adds to the piece price ("+35,00 TL") next to its name. */
  #labelChoices() {
    const [first, ...others] = this.#config?.choices ?? [];
    if (first === undefined) return;

    for (const choice of others) {
      const extras = new Set();
      for (const card of this.#cards.values()) {
        extras.add((card.variants[choice]?.price ?? 0) - (card.variants[first]?.price ?? 0));
      }

      const [extra] = extras;
      const label = extras.size === 1 && extra > 0 ? `+${this.#money(extra)}` : '';
      for (const button of this.querySelectorAll('[data-choice]')) {
        if (button.getAttribute('data-choice') !== choice) continue;

        const target = button.querySelector('[data-choice-extra]');
        if (target) target.textContent = label;
      }
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target || !this.contains(target) || !this.#config) return;

    if (target.matches('[data-plus], [data-minus]')) {
      const id = target.closest('[data-card]')?.getAttribute('data-id');
      if (!id) return;

      const { step } = this.#config;
      const current = this.#quantities.get(id) ?? 0;
      // Step from the nearest multiple so an odd, hand-typed cart quantity lands back on the grid.
      const next = target.matches('[data-plus]')
        ? (Math.floor(current / step) + 1) * step
        : (Math.ceil(current / step) - 1) * step;
      this.#setQuantity(id, next);
    } else if (target.matches('[data-remove]')) {
      const id = target.getAttribute('data-remove');
      if (!id) return;

      this.#setQuantity(id, 0);
      this.querySelector('[data-picked-list] [data-remove]')?.focus({ preventScroll: true });
    } else if (target.matches('[data-filter]')) {
      this.#filter = target.getAttribute('data-filter') ?? '';
      for (const chip of this.querySelectorAll('[data-filter]')) {
        chip.setAttribute('aria-pressed', String(chip === target));
      }
      this.#applyFilter();
    } else if (target.matches('[data-choice]')) {
      this.#choice = target.getAttribute('data-choice') ?? '';
      this.#render();
    } else if (target.matches('[data-shoe]')) {
      this.#setShoe(target.getAttribute('data-shoe') ?? '');
    } else if (target.matches('[data-preview-toggle]')) {
      this.#togglePreview(target);
    } else if (target.matches('[data-preview-pair]')) {
      const preview = target.closest('[data-preview]');
      if (preview) this.#showPair(preview, !preview.hasAttribute('data-pair'));
    } else if (target.matches('[data-submit]')) {
      this.#submit();
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-qty]') || !this.#config) return;

    const id = input.closest('[data-card]')?.getAttribute('data-id');
    if (!id) return;

    const { step } = this.#config;
    const typed = Math.max(0, Math.floor(Number(input.value) || 0));
    this.#setQuantity(id, Math.ceil(typed / step) * step);
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-search]')) return;

    this.#query = searchable(input.value);
    this.#applyFilter();
  };

  /**
   * Follows cart changes made elsewhere (for example in the cart drawer).
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then((result) => {
        if (result?.detail?.source !== EVENT_SOURCE) this.#loadCart(!this.#isDirty());
      })
      .catch(() => {});
  };

  /**
   * Reads this product's quantities from the cart.
   * @param {boolean} adopt - Also use them as the page's chosen quantities.
   */
  async #loadCart(adopt) {
    const config = this.#config;
    if (!config) return;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#controller?.signal,
      }).then((response) => response.json());

      this.#readCart(cart.items);
      if (adopt) this.#adoptCart();
      this.#render();
    } catch {
      // The builder still works from an empty state; the cart is read again after the next update.
    }
  }

  /** @param {{ product_id: number, variant_id: number, quantity: number }[]} [items] */
  #readCart(items = []) {
    this.#cartQuantities.clear();
    for (const item of items) {
      if (item.product_id !== this.#config?.productId) continue;

      const id = String(item.variant_id);
      this.#cartQuantities.set(id, (this.#cartQuantities.get(id) ?? 0) + item.quantity);
    }
  }

  #adoptCart() {
    /** @type {Map<string, number>} */
    const perChoice = new Map();

    this.#quantities.clear();
    for (const [id, card] of this.#cards) {
      let quantity = 0;
      for (const [choice, variant] of Object.entries(card.variants)) {
        const inCart = this.#cartQuantities.get(variant.id) ?? 0;
        quantity += inCart;
        perChoice.set(choice, (perChoice.get(choice) ?? 0) + inCart);
      }

      if (quantity > 0) this.#quantities.set(id, quantity);
      this.#syncCard(id, card);
    }

    // A cart holding more than one choice follows the larger one and is evened out on the next submit.
    let most = 0;
    for (const [choice, quantity] of perChoice) {
      if (quantity <= most) continue;

      most = quantity;
      this.#choice = choice;
    }
  }

  /**
   * The variant a card stands for under the current choice.
   * @param {Card} card
   * @returns {CardVariant | undefined}
   */
  #variantOf(card) {
    return card.variants[this.#choice] ?? Object.values(card.variants)[0];
  }

  /**
   * Quantities the cart should hold, by variant ID.
   * @returns {Map<string, number>}
   */
  #wanted() {
    const wanted = new Map();
    for (const [id, quantity] of this.#quantities) {
      const card = this.#cards.get(id);
      const variant = card ? this.#variantOf(card) : undefined;
      if (variant) wanted.set(variant.id, quantity);
    }
    return wanted;
  }

  /**
   * @param {string} id
   * @param {number} quantity
   */
  #setQuantity(id, quantity) {
    const card = this.#cards.get(id);
    if (!card) return;

    if (quantity > 0) {
      this.#quantities.set(id, quantity);
    } else {
      this.#quantities.delete(id);
    }

    this.#syncCard(id, card);
    this.#render();
    if (this.#filter === PICKED_FILTER) this.#applyFilter();
  }

  /**
   * @param {string} id
   * @param {Card} card
   */
  #syncCard(id, card) {
    const quantity = this.#quantities.get(id) ?? 0;

    card.input.value = String(quantity);
    card.element.toggleAttribute('data-picked', quantity > 0);
    if (card.minus && !card.input.disabled) card.minus.disabled = quantity === 0;
  }

  #applyFilter() {
    let visible = 0;

    for (const [id, card] of this.#cards) {
      const inFilter =
        this.#filter === ''
          ? true
          : this.#filter === PICKED_FILTER
          ? this.#quantities.has(id)
          : card.group === this.#filter;
      const show = inFilter && (this.#query === '' || card.search.includes(this.#query));

      card.element.hidden = !show;
      if (show) visible += 1;
    }

    const empty = this.querySelector('[data-empty]');
    if (empty instanceof HTMLElement && this.#config) {
      const { strings } = this.#config;
      empty.textContent = this.#filter === PICKED_FILTER && this.#query === '' ? strings.noPicked : strings.noResults;
      empty.hidden = visible > 0;
    }
  }

  #isDirty() {
    const wanted = this.#wanted();
    if (wanted.size !== this.#cartQuantities.size) return true;

    for (const [id, quantity] of wanted) {
      if (this.#cartQuantities.get(id) !== quantity) return true;
    }
    return false;
  }

  /** @returns {number} */
  #totalQuantity() {
    let total = 0;
    for (const quantity of this.#quantities.values()) total += quantity;
    return total;
  }

  /** @param {number} amount - Minor units. */
  #money(amount) {
    return this.#config ? formatMoney(amount, this.#config.moneyFormat, this.#config.currency) : '';
  }

  /**
   * @param {string} selector
   * @param {string} text
   */
  #setText(selector, text) {
    for (const target of this.querySelectorAll(selector)) target.textContent = text;
  }

  #render() {
    const config = this.#config;
    const base = config?.tiers[0];
    if (!config || !base) return;

    const { strings, tiers } = config;
    const total = this.#totalQuantity();
    const models = this.#quantities.size;

    let tierIndex = -1;
    tiers.forEach((tier, index) => {
      if (total >= tier.min) tierIndex = index;
    });

    const tier = tiers[tierIndex] ?? base;
    const next = tiers[tierIndex + 1];
    const saving = total * tier.off;

    // Every model has its own list price under the current choice; the tier takes the same amount off each.
    const [firstCard] = this.#cards.values();
    const listPrice = (firstCard ? this.#variantOf(firstCard)?.price : undefined) ?? base.price;
    const pickedPrices = new Set();
    let amount = 0;

    for (const [id, card] of this.#cards) {
      const price = this.#variantOf(card)?.price ?? listPrice;
      const quantity = this.#quantities.get(id) ?? 0;

      if (card.priceLabel) card.priceLabel.textContent = this.#money(price - tier.off);
      if (quantity === 0) continue;

      amount += quantity * (price - tier.off);
      pickedPrices.add(price);
    }

    // One piece price can be named only when the models in the order cost the same.
    /** @type {number | null} */
    let unitPrice = null;
    if (!config.varied) {
      unitPrice = listPrice;
    } else if (pickedPrices.size === 1) {
      [unitPrice] = pickedPrices;
    }

    for (const target of this.querySelectorAll('[data-total-quantity]')) {
      target.textContent =
        target.getAttribute('data-total-quantity') === 'models'
          ? fill(strings.piecesModels, { count: total, models })
          : fill(strings.pieces, { count: total });
    }
    for (const part of this.querySelectorAll('[data-unit-part]')) {
      if (part instanceof HTMLElement) part.hidden = unitPrice === null;
    }
    if (unitPrice !== null) this.#setText('[data-unit-price]', this.#money(unitPrice - tier.off));
    this.#setText('[data-total-price]', this.#money(amount));
    this.#setText('[data-saving]', `−${this.#money(saving)}`);
    this.#setText('[data-picked-count]', String(models));

    for (const button of this.querySelectorAll('[data-choice]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-choice') === this.#choice));
    }

    const savingRow = this.querySelector('[data-saving-row]');
    if (savingRow instanceof HTMLElement) savingRow.hidden = saving <= 0;

    for (const item of this.querySelectorAll('[data-tier]')) {
      const itemTier = tiers.find((candidate) => candidate.min === Number(item.getAttribute('data-tier')));
      if (!itemTier) continue;

      if (tierIndex >= 0 && itemTier === tier) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }

      const price = item.querySelector('[data-tier-price]');
      if (!price) continue;

      if (!config.varied) {
        price.textContent = this.#money(listPrice - itemTier.off);
      } else {
        price.textContent =
          itemTier.off > 0 ? fill(strings.tierOff, { amount: this.#money(itemTier.off) }) : strings.tierList;
      }
    }

    // Next-tier hint: how many more pieces lower the piece price, and by how much the order total drops.
    let nudge = strings.topTier;
    let progress = 1;

    if (total === 0) {
      nudge = fill(strings.start, { min: config.min });
      progress = 0;
    } else if (total < config.min) {
      nudge = fill(strings.belowMin, { min: config.min, count: config.min - total });
      progress = total / config.min;
    } else if (next) {
      const count = next.min - total;

      if (config.varied) {
        nudge = fill(strings.nextTierOff, { count, amount: this.#money(next.off) });
      } else {
        const nextPrice = this.#money(listPrice - next.off);
        const drop = amount - next.min * (listPrice - next.off);

        nudge =
          drop > 0
            ? fill(strings.nextTierCheaper, { count, price: nextPrice, saving: this.#money(drop) })
            : fill(strings.nextTier, { count, price: nextPrice });
      }
      progress = (total - tier.min) / (next.min - tier.min);
    }

    this.#setText('[data-nudge]', nudge);
    for (const meter of this.querySelectorAll('[data-meter]')) {
      if (meter instanceof HTMLElement) meter.style.width = `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%`;
    }

    this.#renderPicked();
    this.#renderPreview(total);
    this.#renderSubmit(total);
  }

  /**
   * Shows the chosen charms in the holes of the clog drawings, one image per piece, following
   * the arrangement (pick order, changed by dragging); the note says how many did not fit.
   * @param {number} total - Pieces chosen on the page.
   */
  #renderPreview(total) {
    const previews = this.querySelectorAll('[data-preview]');
    if (previews.length === 0 || !this.#config) return;

    const { strings } = this.#config;
    this.#reconcile();

    for (const preview of previews) {
      const slots = preview.querySelectorAll('[data-slot]');
      // The phone strip hides its second clog until "Çift" is on; the first time the charms
      // outgrow one clog it opens the pair by itself.
      const pairButton = preview.querySelector('[data-preview-pair]');
      let visible = slots.length;
      if (pairButton && !preview.hasAttribute('data-pair')) {
        visible = Math.floor(slots.length / 2);
        if (total > visible && !preview.hasAttribute('data-pair-auto')) {
          preview.setAttribute('data-pair-auto', '');
          this.#showPair(preview, true);
          visible = slots.length;
        }
      }

      let placed = 0;
      slots.forEach((slot, index) => {
        if (!(slot instanceof SVGElement)) return;

        const id = this.#arrangement[index];
        const face = id ? this.#cards.get(id)?.face : undefined;
        if (face) {
          if (slot.getAttribute('href') !== face) slot.setAttribute('href', face);
          slot.style.display = '';
          if (index < visible) placed += 1;
        } else {
          slot.style.display = 'none';
          slot.removeAttribute('href');
        }
      });

      const more = total - placed;
      const note = preview.querySelector('[data-preview-note]');
      if (note) {
        note.textContent =
          total === 0 ? strings.previewHint : more > 0 ? fill(strings.previewMore, { count: more }) : strings.previewDrag;
      }

      const count = preview.querySelector('[data-preview-count]');
      if (count) count.textContent = total > 0 ? fill(strings.previewCount, { count: total }) : '';
    }

    // The first charm on the clog teaches dragging: a short "finger moves it next door and
    // back" demo, then the hint under the clog lights up for a while. Once per visit.
    if (total > 0 && !this.#demoDone && !this.#drag) {
      let seen = false;
      try {
        seen = sessionStorage.getItem(DEMO_KEY) === '1';
      } catch {
        // Storage can be unavailable; the demo then plays on every page load.
      }
      if (!seen) {
        this.#demoDone = true;
        setTimeout(() => this.#demoDrag(), 700);
      }
    }
  }

  /**
   * Plays the drag demo on the visible clog: a touch marker lands on the first placed charm,
   * carries a copy of it to the nearest empty hole and back, then the hint is highlighted.
   * Interrupted by a real drag or a quantity change.
   */
  async #demoDrag() {
    const preview = [...this.querySelectorAll('[data-preview]')].find((candidate) => {
      const svg = candidate.querySelector('svg');
      return svg && svg.getBoundingClientRect().width > 0 && !candidate.hasAttribute('data-collapsed');
    });
    if (!preview || this.#drag) return;

    const slots = [...preview.querySelectorAll('[data-slot]')];
    const index = this.#arrangement.findIndex((id) => id);
    const slot = slots[index];
    if (!(slot instanceof SVGImageElement) || slot.style.display === 'none') return;

    const svg = slot.ownerSVGElement;
    if (!svg) return;

    const from = { x: Number(slot.dataset.cx), y: Number(slot.dataset.cy) };
    /** @type {{ x: number, y: number } | null} */
    let to = null;
    let best = Infinity;
    slots.forEach((other, otherIndex) => {
      if (other.ownerSVGElement !== svg || this.#arrangement[otherIndex] || !(other instanceof SVGImageElement)) return;

      const point = { x: Number(other.dataset.cx), y: Number(other.dataset.cy) };
      const distance = Math.hypot(point.x - from.x, point.y - from.y);
      if (distance < best) {
        best = distance;
        to = point;
      }
    });

    try {
      sessionStorage.setItem(DEMO_KEY, '1');
    } catch {
      // Fine without storage.
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (to && !reduced) {
      const target = to;
      const ghost = slot.cloneNode();
      const touch = document.createElementNS(SVG_NS, 'circle');
      if (ghost instanceof SVGImageElement) {
        const size = Number(slot.getAttribute('width')) * 1.15;
        ghost.removeAttribute('data-slot');
        ghost.classList.add('puf-crocs__charm--drag');
        ghost.setAttribute('width', String(size));
        ghost.setAttribute('height', String(size));
        touch.setAttribute('class', 'puf-crocs__touch');
        touch.setAttribute('r', '10');
        touch.style.opacity = '0';
        svg.append(ghost, touch);
        slot.classList.add('puf-crocs__charm--lifted');

        /** @param {{ x: number, y: number }} point */
        const place = (point) => {
          ghost.setAttribute('x', String(point.x - size / 2));
          ghost.setAttribute('y', String(point.y - size / 2));
          ghost.setAttribute('transform', `rotate(90 ${point.x} ${point.y})`);
          touch.setAttribute('cx', String(point.x));
          touch.setAttribute('cy', String(point.y));
        };
        /**
         * @param {{ x: number, y: number }} a
         * @param {{ x: number, y: number }} b
         * @param {number} t
         */
        const between = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

        let cancelled = false;
        this.#demoCancel = () => {
          cancelled = true;
        };
        /**
         * @param {number} duration - ms
         * @param {(progress: number) => void} frame
         */
        const tween = (duration, frame) =>
          new Promise((resolve) => {
            const start = performance.now();
            // Timer-driven rather than requestAnimationFrame so the demo also finishes (and
            // cleans up) when the tab is hidden or frames are throttled.
            const tick = () => {
              if (cancelled) {
                resolve();
                return;
              }
              const progress = Math.min(1, (performance.now() - start) / duration);
              frame(progress);
              if (progress < 1) {
                setTimeout(tick, 16);
              } else {
                resolve();
              }
            };
            tick();
          });

        place(from);
        await tween(400, (p) => {
          touch.style.opacity = String(p);
        });
        await tween(900, (p) => place(between(from, target, ease(p))));
        await tween(500, () => {});
        await tween(900, (p) => place(between(target, from, ease(p))));
        await tween(350, (p) => {
          touch.style.opacity = String(1 - p);
        });

        ghost.remove();
        touch.remove();
        slot.classList.remove('puf-crocs__charm--lifted');
        this.#demoCancel = null;
        if (cancelled) return;
      }
    }

    // Highlight the hint for a while (every preview carries one; only the visible one shows).
    clearTimeout(this.#hintTimer);
    for (const note of this.querySelectorAll('[data-preview-note]')) note.classList.add('puf-crocs__note--hint');
    this.#hintTimer = setTimeout(() => {
      for (const note of this.querySelectorAll('[data-preview-note]')) note.classList.remove('puf-crocs__note--hint');
    }, HINT_DURATION);
  }

  /**
   * Brings the hole arrangement in line with the chosen quantities: pieces no longer chosen
   * leave their holes, new pieces take the first free holes (middle of the clog first), and
   * pieces the shopper dragged stay where they were put.
   */
  #reconcile() {
    /** @type {Map<string, number>} */
    const wanted = new Map();
    for (const [id, quantity] of this.#quantities) {
      if (this.#cards.get(id)?.face) wanted.set(id, Math.min(quantity, PREVIEW_LIMIT));
    }

    /** @type {Map<string, number>} */
    const placed = new Map();
    this.#arrangement.forEach((id, index) => {
      if (!id) return;

      const count = placed.get(id) ?? 0;
      if (count < (wanted.get(id) ?? 0)) {
        placed.set(id, count + 1);
      } else {
        this.#arrangement[index] = null;
      }
    });

    for (const [id, quantity] of wanted) {
      let count = placed.get(id) ?? 0;
      for (let index = 0; index < this.#arrangement.length && count < quantity; index += 1) {
        if (this.#arrangement[index]) continue;

        this.#arrangement[index] = id;
        count += 1;
      }
    }
  }

  /**
   * Starts dragging a charm on the clog: a copy follows the pointer until it is dropped on
   * another hole (swapping with whatever sits there) or let go elsewhere.
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    const slot = event.target instanceof Element ? event.target.closest('[data-slot]') : null;
    if (!(slot instanceof SVGImageElement) || this.#drag || !slot.getAttribute('href')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const preview = slot.closest('[data-preview]');
    const svg = slot.ownerSVGElement;
    if (!preview || !svg) return;

    const index = [...preview.querySelectorAll('[data-slot]')].indexOf(slot);
    if (index < 0 || !this.#arrangement[index]) return;

    event.preventDefault();
    this.#demoCancel?.();
    const ghost = slot.cloneNode();
    if (!(ghost instanceof SVGImageElement)) return;

    const size = Number(slot.getAttribute('width')) * 1.15;
    ghost.removeAttribute('data-slot');
    ghost.classList.add('puf-crocs__charm--drag');
    ghost.setAttribute('width', String(size));
    ghost.setAttribute('height', String(size));
    svg.append(ghost);
    slot.classList.add('puf-crocs__charm--lifted');
    svg.setPointerCapture(event.pointerId);

    this.#drag = { index, slot, ghost, svg, preview, pointerId: event.pointerId, target: null };
    this.#followPointer(event);
  };

  /** @param {Event} event */
  #onContextMenu = (event) => {
    if (event.target instanceof Element && event.target.closest('.puf-crocs__shoe')) event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (this.#drag && event.pointerId === this.#drag.pointerId) this.#followPointer(event);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (this.#drag && event.pointerId === this.#drag.pointerId) this.#endDrag(true);
  };

  /** @param {PointerEvent} event */
  #onPointerCancel = (event) => {
    if (this.#drag && event.pointerId === this.#drag.pointerId) this.#endDrag(false);
  };

  /**
   * Moves the dragged copy under the pointer and marks the nearest hole of the preview
   * (either clog on desktop) within about one charm width.
   * @param {PointerEvent} event
   */
  #followPointer(event) {
    const drag = this.#drag;
    if (!drag) return;

    const matrix = drag.svg.getScreenCTM()?.inverse();
    if (matrix) {
      const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix);
      const size = Number(drag.ghost.getAttribute('width'));
      drag.ghost.setAttribute('x', String(point.x - size / 2));
      drag.ghost.setAttribute('y', String(point.y - size / 2));
      drag.ghost.setAttribute('transform', `rotate(90 ${point.x} ${point.y})`);
    }

    const holes = drag.preview.querySelectorAll('[data-hole]');
    let best = drag.slot.getBoundingClientRect().width * 0.8;
    /** @type {number | null} */
    let target = null;
    holes.forEach((hole, index) => {
      const rect = hole.getBoundingClientRect();
      if (rect.width === 0) return; // hole of a hidden clog

      const distance = Math.hypot(rect.left + rect.width / 2 - event.clientX, rect.top + rect.height / 2 - event.clientY);
      if (distance < best) {
        best = distance;
        target = index;
      }
    });

    holes.forEach((hole, index) => hole.classList.toggle('puf-crocs__hole--target', index === target && index !== drag.index));
    drag.target = target;
  }

  /**
   * Ends a drag: drops the charm into the marked hole, swapping with the charm there, or puts
   * it back.
   * @param {boolean} drop
   */
  #endDrag(drop) {
    const drag = this.#drag;
    if (!drag) return;

    this.#drag = null;
    drag.ghost.remove();
    drag.slot.classList.remove('puf-crocs__charm--lifted');
    for (const hole of drag.preview.querySelectorAll('[data-hole]')) hole.classList.remove('puf-crocs__hole--target');
    if (drag.svg.hasPointerCapture(drag.pointerId)) drag.svg.releasePointerCapture(drag.pointerId);

    const { index, target } = drag;
    if (drop && target !== null && target !== index) {
      const moved = this.#arrangement[index];
      this.#arrangement[index] = this.#arrangement[target] ?? null;
      this.#arrangement[target] = moved;
    }
    this.#renderPreview(this.#totalQuantity());
  }

  /**
   * Recolours the clog drawings and marks the matching swatch.
   * @param {string} color - CSS colour from the swatch.
   */
  #setShoe(color) {
    if (!color) return;

    for (const preview of this.querySelectorAll('[data-preview]')) {
      if (preview instanceof HTMLElement) preview.style.setProperty(SHOE_PROPERTY, color);
    }
    for (const swatch of this.querySelectorAll('[data-shoe]')) {
      swatch.setAttribute('aria-pressed', String(swatch.getAttribute('data-shoe') === color));
    }
  }

  /**
   * Shows or hides the second clog of the phone strip.
   * @param {Element} preview
   * @param {boolean} pair
   */
  #showPair(preview, pair) {
    preview.toggleAttribute('data-pair', pair);
    for (const button of preview.querySelectorAll('[data-preview-pair]')) {
      button.setAttribute('aria-pressed', String(pair));
    }
  }

  /**
   * Folds the phone preview strip to a single line and back.
   * @param {HTMLButtonElement} button
   */
  #togglePreview(button) {
    const preview = button.closest('[data-preview]');
    if (!preview) return;

    const collapsed = preview.toggleAttribute('data-collapsed');
    button.setAttribute('aria-expanded', String(!collapsed));
    for (const part of button.querySelectorAll('[data-toggle-collapse], [data-toggle-expand]')) {
      if (part instanceof HTMLElement) part.hidden = part.hasAttribute('data-toggle-collapse') ? collapsed : !collapsed;
    }
  }

  #renderPicked() {
    const list = this.querySelector('[data-picked-list]');
    if (!list || !this.#config) return;

    const { strings } = this.#config;
    const rows = [];

    for (const [id, card] of this.#cards) {
      const quantity = this.#quantities.get(id);
      if (!quantity) continue;

      const row = document.createElement('li');
      row.className = 'puf-stand__picked-item';

      const name = document.createElement('span');
      name.className = 'puf-stand__picked-name';
      name.textContent = card.name;

      const amount = document.createElement('span');
      amount.className = 'puf-stand__picked-quantity';
      amount.textContent = fill(strings.pieces, { count: quantity });

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'puf-stand__remove';
      remove.setAttribute('data-remove', id);
      remove.setAttribute('aria-label', fill(strings.remove, { name: card.name }));
      remove.textContent = '×';

      row.append(name, amount, remove);
      rows.push(row);
    }

    if (rows.length === 0) {
      const row = document.createElement('li');
      row.className = 'puf-stand__picked-item puf-stand__picked-empty';
      row.textContent = strings.noPicked;
      rows.push(row);
    }

    list.replaceChildren(...rows);
  }

  /** @param {number} total */
  #renderSubmit(total) {
    const config = this.#config;
    if (!config) return;

    const { strings } = config;
    const inCart = this.#cartQuantities.size > 0;
    const dirty = this.#isDirty();
    const belowMin = total > 0 && total < config.min;

    let label = inCart ? strings.update : strings.add;
    let disabled = !dirty || belowMin;

    if (belowMin) {
      label = fill(strings.needMore, { count: config.min - total });
    } else if (!dirty && inCart) {
      label = strings.viewCart;
      disabled = false;
    }

    for (const button of this.querySelectorAll('[data-submit]')) {
      if (!(button instanceof HTMLButtonElement)) continue;

      button.textContent = label;
      button.disabled = disabled || this.#busy;
      button.toggleAttribute('aria-busy', this.#busy);
    }
  }

  async #submit() {
    const config = this.#config;
    if (!config || this.#busy) return;

    if (!this.#isDirty()) {
      window.location.assign(Theme.routes.cart_url);
      return;
    }

    const wanted = this.#wanted();
    /** @type {Record<string, number>} */
    const updates = {};
    for (const id of new Set([...wanted.keys(), ...this.#cartQuantities.keys()])) {
      const quantity = wanted.get(id) ?? 0;
      if (quantity !== (this.#cartQuantities.get(id) ?? 0)) updates[id] = quantity;
    }

    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    const added = Object.entries(updates).filter(([, quantity]) => quantity > 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.#busy = true;
    this.#renderSubmit(this.#totalQuantity());

    this.dispatchEvent(
      added.length > 0
        ? new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: added.map(([id, quantity]) => ({ merchandiseId: id, quantity })),
            promise: deferred.promise,
          })
        : new CartLinesUpdateEvent({
            action: 'update',
            context: 'product',
            lines: Object.keys(updates).map((id) => ({ id, quantity: 0 })),
            promise: deferred.promise,
          })
    );

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      const didError = !response.ok || Boolean(result.status);

      if (didError) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Cart update failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        // Resolve with the unchanged cart so the cart icon and drawer keep their real state.
        const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
          (cartResponse) => cartResponse.json()
        );
        this.#readCart(cart.items);
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: EVENT_SOURCE, itemCount: cart.item_count, didError },
        });
        this.#announce(result.description || result.message || config.strings.error);
        return;
      }

      this.#readCart(result.items);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: {
          items: result.items,
          source: EVENT_SOURCE,
          itemCount: result.item_count,
          sections: result.sections,
          didError,
        },
      });
      this.#announce(config.strings.updated);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during cart update', code: 'SERVICE_UNAVAILABLE' }));
      this.#announce(config.strings.error);
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  /** @param {string} message */
  #announce(message) {
    this.#setText('[data-status]', message);
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#setText('[data-status]', ''), STATUS_CLEAR_DELAY);
  }
}

if (!customElements.get('puf-stand-builder')) customElements.define('puf-stand-builder', PufStandBuilder);
